/* R&R UI polish: predictive overlay search + viewport-safe custom selects. */
(() => {
  if (window.__rrUiPolishLoaded) return;
  window.__rrUiPolishLoaded = true;

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];
  const nextFrame = fn => requestAnimationFrame(() => requestAnimationFrame(fn));

  function clampOpenSelect(wrap) {
    if (!wrap?.classList.contains('is-open')) return;
    const list = qs('.rr-select-options:not([hidden])', wrap);
    const button = qs('.rr-select-trigger', wrap);
    if (!list || !button) return;

    list.style.transform = 'translateX(0px)';
    list.style.maxWidth = `${Math.max(180, window.innerWidth - 24)}px`;
    list.style.minWidth = `${Math.ceil(button.getBoundingClientRect().width)}px`;

    const buttonRect = button.getBoundingClientRect();
    const desiredHeight = Math.min(300, Math.max(140, list.scrollHeight));
    const spaceBelow = Math.max(0, window.innerHeight - buttonRect.bottom - 14);
    const spaceAbove = Math.max(0, buttonRect.top - 14);
    const useAbove = spaceBelow < Math.min(180, desiredHeight) && spaceAbove > spaceBelow;
    wrap.classList.toggle('rr-select--above', useAbove);
    const availableHeight = useAbove ? spaceAbove : spaceBelow;
    list.style.maxHeight = `${Math.max(110, Math.min(300, availableHeight - 4))}px`;

    const rect = list.getBoundingClientRect();
    const gutter = 12;
    let shift = 0;
    if (rect.left < gutter) shift += gutter - rect.left;
    if (rect.right + shift > window.innerWidth - gutter) shift -= (rect.right + shift) - (window.innerWidth - gutter);
    list.style.transform = `translateX(${Math.round(shift)}px)`;
  }

  function clampOpenSelects() {
    qsa('.rr-select.is-open').forEach(clampOpenSelect);
  }

  const queueClamp = () => nextFrame(clampOpenSelects);
  document.addEventListener('click', queueClamp);
  document.addEventListener('keydown', queueClamp);
  window.addEventListener('resize', queueClamp, {passive:true});
  window.addEventListener('orientationchange', queueClamp, {passive:true});
  window.addEventListener('scroll', queueClamp, {passive:true, capture:true});

  function setupSearch() {
    const dialog = qs('[data-rr-search-dialog]');
    if (!dialog || dialog.dataset.rrReady) return;
    dialog.dataset.rrReady = 'true';
    const input = qs('[data-rr-search-input]', dialog);
    const form = qs('[data-rr-search-form]', dialog);
    const results = qs('[data-rr-search-results]', dialog);
    const status = qs('[data-rr-search-status]', dialog);
    let returnFocus = null;
    let requestId = 0;
    let timer = 0;

    const setStatus = message => { if (status) status.textContent = message; };
    const clearResults = () => { if (results) results.replaceChildren(); };
    const activeCurrency = () => window.Shopify?.currency?.active || 'USD';
    const locale = () => document.documentElement.lang || 'en-US';
    const money = value => {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return '';
      try { return new Intl.NumberFormat(locale(), {style:'currency', currency:activeCurrency()}).format(amount); }
      catch { return `$${amount.toFixed(2)}`; }
    };

    function closeSearch() {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
    }

    function openSearch(trigger) {
      returnFocus = trigger || document.activeElement;
      qsa('[data-rr-menu][open]').forEach(menu => { menu.open = false; });
      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open','');
      }
      document.documentElement.classList.add('rr-search-open');
      document.body?.classList.add('rr-search-open');
      setTimeout(() => { input?.focus(); input?.select?.(); }, 40);
    }

    qsa('[data-rr-open-search]').forEach(button => button.addEventListener('click', () => openSearch(button)));
    qsa('[data-rr-close-search]', dialog).forEach(button => button.addEventListener('click', closeSearch));
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('rr-search-open');
      document.body?.classList.remove('rr-search-open');
      returnFocus?.focus?.();
    });
    dialog.addEventListener('click', event => { if (event.target === dialog) closeSearch(); });

    function createResult(product) {
      const link = document.createElement('a');
      link.className = 'rr-search-result';
      link.href = product.url || '#';

      const media = document.createElement('span');
      media.className = 'rr-search-result-image';
      const imageUrl = product.featured_image?.url || product.image || '';
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = product.featured_image?.alt || product.title || '';
        image.loading = 'lazy';
        media.append(image);
      }
      link.append(media);

      const copy = document.createElement('span');
      const title = document.createElement('span');
      title.className = 'rr-search-result-title';
      title.textContent = product.title || 'R&R product';
      copy.append(title);
      const metaText = [product.type, product.vendor].filter(Boolean).join(' · ');
      if (metaText) {
        const meta = document.createElement('span');
        meta.className = 'rr-search-result-meta';
        meta.textContent = metaText;
        copy.append(meta);
      }
      link.append(copy);

      const priceText = money(product.price_min ?? product.price);
      if (priceText) {
        const price = document.createElement('span');
        price.className = 'rr-search-result-price';
        price.textContent = priceText;
        link.append(price);
      }
      return link;
    }

    async function runSearch(rawQuery) {
      const query = String(rawQuery || '').trim();
      const id = ++requestId;
      clearResults();
      if (!query) { setStatus('Start typing to search R&R.'); return; }
      setStatus('Searching…');
      try {
        const root = window.Shopify?.routes?.root || '/';
        const endpoint = new URL(`${root}search/suggest.json`, window.location.origin);
        endpoint.searchParams.set('q', query);
        endpoint.searchParams.set('resources[type]', 'product');
        endpoint.searchParams.set('resources[limit]', '8');
        endpoint.searchParams.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(endpoint, {headers:{Accept:'application/json'}, credentials:'same-origin'});
        if (!response.ok) throw new Error('Search request failed');
        const data = await response.json();
        if (id !== requestId) return;
        const products = data?.resources?.results?.products || [];
        if (!products.length) {
          setStatus(`No matches for “${query}”. Try lotion, body gloss, shampoo, or 5 in 1.`);
          return;
        }
        setStatus(`${products.length} ${products.length === 1 ? 'match' : 'matches'}.`);
        const fragment = document.createDocumentFragment();
        products.forEach(product => fragment.append(createResult(product)));
        results?.append(fragment);
      } catch (error) {
        if (id !== requestId) return;
        setStatus('Search is having a moment. Try again, or refresh the page.');
      }
    }

    input?.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => runSearch(input.value), 170);
    });
    form?.addEventListener('submit', event => {
      event.preventDefault();
      clearTimeout(timer);
      runSearch(input?.value || '');
    });
  }

  function init() {
    setupSearch();
    queueClamp();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true}); else init();
  document.addEventListener('shopify:section:load', init);
})();

/* R&R mobile menu backdrop: dim and blur the page like search while keeping the menu glass above it. */
;(() => {
  if (window.__rrMenuBackdropLoaded) return;
  window.__rrMenuBackdropLoaded = true;

  const menus = () => [...document.querySelectorAll('.rr-menu[data-rr-menu]')];
  let backdrop = null;

  const sync = () => {
    if (!backdrop) return;
    const open = menus().some(menu => menu.open);
    backdrop.hidden = !open;
    document.body?.classList.toggle('rr-menu-open', open);
  };

  const setup = () => {
    const currentMenus = menus();
    if (!currentMenus.length) {
      document.body?.classList.remove('rr-menu-open');
      if (backdrop) backdrop.hidden = true;
      return;
    }

    backdrop = document.querySelector('.rr-menu-backdrop');
    if (!backdrop) {
      backdrop = document.createElement('button');
      backdrop.type = 'button';
      backdrop.className = 'rr-menu-backdrop';
      backdrop.setAttribute('aria-label', 'Close menu');
      backdrop.hidden = true;
      document.body.append(backdrop);
      backdrop.addEventListener('click', () => {
        menus().forEach(menu => { menu.open = false; });
        sync();
      });
    }

    currentMenus.forEach(menu => {
      if (menu.dataset.rrBackdropReady === 'true') return;
      menu.dataset.rrBackdropReady = 'true';
      menu.addEventListener('toggle', sync);
    });
    sync();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, {once:true}); else setup();
  document.addEventListener('shopify:section:load', setup);
  document.addEventListener('shopify:section:unload', sync);
})();
